/**
 * CreateActionCard - v2 content for CRM object creation actions.
 *
 * Renders type-specific content (contact, company, deal) via the
 * ENTITY_CONTENT_REGISTRY lookup, falling back to EntityCard for
 * unknown entity types.
 *
 * When the parent ActionAccordion is in edit mode (isEditing = true),
 * delegates to CreateActionEditForm for inline field editing.
 *
 * Shell (title, checkbox, expand/collapse) is provided by the parent
 * (ActionAccordion or ActionVersionTimeline).
 */

import { CrmClickableContent } from '@/lib/crm/object-cards/CrmClickableContent';
import { isMemoEntityType } from '@/lib/crm/record/memo/notes.utils';
import { TASK_ENTITY_TYPE } from '@/lib/crm/record/task/task.constants';

import type {
  ObjectCreatedEntityPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { isObjectCreatedPayload } from '../../lib/proposals/core/types';
import type { ActionRendererProps } from '../../lib/proposals/registry/types';
import { useActionEdit } from './ActionAccordion';
import {
  ENTITY_CONTENT_REGISTRY,
  extractProperties,
} from './CreateActionCard.helpers';
import { CreateActionEditForm } from './CreateActionEditForm';
import { EntityTypeEditForm } from './EntityTypeEditForm';
import { GenericEntityContent } from './GenericEntityContent';
import { NoteCreateActionCard } from './NoteCreateActionCard';
import { PropertyTypeEditForm } from './PropertyTypeEditForm';
import { ActionCardContent, ActionError, computeObjectLabel } from './shared';
import { TaskCreateActionCard } from './TaskCreateActionCard';

// ─── Component ───────────────────────────────────────────────────────────────

export function CreateActionCard({
  action,
  mode,
  enrichedData,
  proposalId,
  orgId,
  onUpdate,
}: ActionRendererProps) {
  const { isEditing } = useActionEdit();

  if (!isObjectCreatedPayload(action.payload)) {
    return (
      <ActionError message='Invalid object creation payload' mode={mode} />
    );
  }

  const typedAction = action as ProposedAction<ObjectCreatedEntityPayload>;
  const { entity_type_name } = typedAction.payload;
  const entityType = enrichedData?.entity_type_name || entity_type_name;

  // Notes and tasks bypass the generic registry path — no accordion Edit
  // button, no CreateActionEditForm — and each renders through its own card:
  // a task through TaskCreateActionCard (TaskCard, every value click-to-edit),
  // a note through NoteCreateActionCard (NoteCard and its edit form). Both go
  // read-only when `onUpdate` is undefined (readonly mode), so it is forwarded
  // as-is. ActionRenderer only passes `onUpdate` when the renderer supports
  // editing AND mode === 'full'.
  if (isMemoEntityType(entityType)) {
    return (
      <ActionCardContent
        contextMessage={typedAction.payload.context_message}
        orgId={orgId}
        resolvedSlots={typedAction.enriched_data?.resolved_slots}
      >
        {entityType === TASK_ENTITY_TYPE ? (
          <TaskCreateActionCard
            action={typedAction}
            proposalId={proposalId}
            orgId={orgId}
            onUpdate={onUpdate}
          />
        ) : (
          <NoteCreateActionCard
            action={typedAction}
            proposalId={proposalId}
            orgId={orgId}
            onUpdate={onUpdate}
          />
        )}
      </ActionCardContent>
    );
  }

  // Edit mode — delegate to the appropriate edit form
  if (isEditing) {
    // Property type creation uses the shared property form (same form as
    // the CRM "create custom property" flow) instead of the generic
    // schema-driven field list.
    if (entityType === 'ahoy_property_type') {
      return (
        <PropertyTypeEditForm
          action={typedAction}
          proposalId={proposalId}
          orgId={orgId}
          onUpdate={onUpdate}
        />
      );
    }
    // Entity type creation uses a dedicated form with the icon picker
    // (same picker as CreateObjectTypeWizard / EditEntityTypeSlideout).
    if (entityType === 'ahoy_entity_type') {
      return (
        <EntityTypeEditForm
          action={typedAction}
          proposalId={proposalId}
          orgId={orgId}
          onUpdate={onUpdate}
        />
      );
    }
    return (
      <CreateActionEditForm
        action={typedAction}
        enrichedData={enrichedData}
        proposalId={proposalId}
        orgId={orgId}
        onUpdate={onUpdate}
      />
    );
  }

  const editedProperties = extractProperties(
    typedAction.payload.payload.properties
  );

  // Prioritize properties over enrichedData.object_label to avoid stale
  // labels after edits (per AC: "flatProps over enrichedData.object_label")
  const label =
    computeObjectLabel(editedProperties, entityType) ||
    enrichedData?.object_label ||
    `New ${entityType.replace(/^ahoy_/, '')}`;

  // Look up the entity-specific content component from the registry
  const ContentComponent = ENTITY_CONTENT_REGISTRY[entityType];
  const crmEntityId = typedAction.payload.entity_id;

  return (
    <ActionCardContent
      contextMessage={typedAction.payload.context_message}
      orgId={orgId}
      resolvedSlots={typedAction.enriched_data?.resolved_slots}
    >
      {ContentComponent ? (
        <CrmClickableContent entityId={crmEntityId}>
          <ContentComponent
            flatProps={editedProperties}
            enrichedData={enrichedData}
            orgId={orgId}
          />
        </CrmClickableContent>
      ) : (
        // Custom / unregistered entity types get the schema-driven generic
        // card (display name + top fields) instead of a bare icon+label.
        <CrmClickableContent entityId={crmEntityId}>
          <GenericEntityContent
            entityType={entityType}
            label={label}
            flatProps={editedProperties}
            orgId={orgId}
          />
        </CrmClickableContent>
      )}
    </ActionCardContent>
  );
}
